import { env } from '@/util/env.utils';
import { isSSR } from '@/util/isSSR';
import { ApolloClient, createHttpLink, from, InMemoryCache } from '@apollo/client';
import { RetryLink } from '@apollo/client/link/retry';
import type { NextRequest } from 'next/server';
import { getNewApolloCache } from './cache';
import { authLink, splitLink } from './httpLink';
import { typeDefs } from './localSchema';

export interface GetNewApolloClientProps {
  req?: NextRequest;
  ssr?: boolean;
}

export const apolloCache = getNewApolloCache();

// Create a custom retry link that avoids retrying known server errors
const retryLink = new RetryLink({
  delay: {
    initial: 500,
    max: 2000,
    jitter: true,
  },
  attempts: {
    max: 2, // Reduce max retries
    retryIf: (error, _operation) => {
      // Don't retry server errors
      if (error?.statusCode >= 500) {
        return false;
      }

      // Default - retry network and connection errors
      return Boolean(error);
    },
  },
});

export const getNewApolloClient = ({ ssr, req }: GetNewApolloClientProps) => {
  if (ssr && isSSR()) {
    // Allow for internal cluster API calls if setup
    const apiDomain = env('NEXT_PUBLIC_API_INTERNAL')
      ? `http://${env('NEXT_PUBLIC_API_INTERNAL')}`
      : `${env('NEXT_PUBLIC_API_PROTOCOL')}://${env('NEXT_PUBLIC_API_DOMAIN')}`;

    return new ApolloClient({
      ssrMode: true,
      link: from([
        retryLink,
        createHttpLink({
          uri: `${apiDomain}/graphql`,
          fetchOptions: {},
          headers: {
            Cookie: req.headers.get('Cookie'),
          },
        }),
      ]),
      cache: new InMemoryCache(),
      typeDefs,
      defaultOptions: {
        query: {
          errorPolicy: 'all',
          fetchPolicy: 'cache-first',
        },
        mutate: {
          errorPolicy: 'all',
        },
        watchQuery: {
          errorPolicy: 'all',
          fetchPolicy: 'cache-and-network',
        },
      },
    });
  }

  return new ApolloClient({
    link: from([retryLink, authLink.concat(splitLink)]),
    cache: apolloCache,
    typeDefs,
    connectToDevTools: true,
    defaultOptions: {
      query: {
        errorPolicy: 'all',
        fetchPolicy: 'cache-first',
      },
      mutate: {
        errorPolicy: 'all',
      },
      watchQuery: {
        errorPolicy: 'all',
        fetchPolicy: 'cache-and-network',
      },
    },
  });
};

export const apolloClientSideClient = getNewApolloClient({ ssr: false });
